import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  Activity,
  AlertCircle,
  ArrowRight,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Filter,
  Globe,
  HelpCircle,
  History,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Star,
  Trash2,
  TrendingUp,
  Wand2,
  X,
} from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import AIBadge from '../../components/AIBadge';
import InfoTip from '../../components/InfoTip';
import ErrorBanner from '../../components/ErrorBanner';
import CsvExportButton from '../../components/CsvExportButton';
import { toast } from '../../lib/toast';
import { formatRelative } from '../../lib/format';

const CHANNEL_META = {
  ai: {
    label: 'AI',
    icon: Bot,
    class: 'bg-brand-50 text-brand-700 border-brand-200',
  },
  google: {
    label: 'Google',
    icon: Search,
    class: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  both: {
    label: 'Both',
    icon: Sparkles,
    class: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  unknown: {
    label: 'Unknown',
    icon: HelpCircle,
    class: 'bg-gray-100 text-gray-600 border-gray-200',
  },
};

const DIFFICULTY_META = {
  low: { label: 'Low', class: 'bg-emerald-50 text-emerald-700' },
  medium: { label: 'Medium', class: 'bg-amber-50 text-amber-700' },
  high: { label: 'High', class: 'bg-rose-50 text-rose-700' },
  unknown: { label: '—', class: 'bg-gray-100 text-gray-600' },
};

const INTENT_LABEL = {
  informational: 'Info',
  commercial: 'Commercial',
  navigational: 'Nav',
  transactional: 'Transactional',
  unknown: '—',
};

const STATUS_META = {
  researching: { label: 'Researching', class: 'bg-gray-100 text-gray-700' },
  targeted: { label: 'Targeted', class: 'bg-brand-50 text-brand-700' },
  won: { label: 'Won', class: 'bg-emerald-50 text-emerald-700' },
  archived: { label: 'Archived', class: 'bg-gray-100 text-gray-500' },
};

export default function KeywordsTab({ domain }) {
  const qc = useQueryClient();
  const [seed, setSeed] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const latestResearchQuery = useQuery({
    queryKey: ['keyword-research-latest', domain._id],
    queryFn: () =>
      api
        .get(`/api/domains/${domain._id}/keywords/research/latest`)
        .then((r) => r.data.research),
  });

  const researchHistoryQuery = useQuery({
    queryKey: ['keyword-research-history', domain._id],
    queryFn: () =>
      api
        .get(`/api/domains/${domain._id}/keywords/research`)
        .then((r) => r.data.runs),
  });

  const targetsQuery = useQuery({
    queryKey: ['keyword-targets', domain._id],
    queryFn: () =>
      api.get(`/api/domains/${domain._id}/keywords`).then((r) => r.data.targets),
  });

  const researchMutation = useMutation({
    mutationFn: (body) =>
      api
        .post(`/api/domains/${domain._id}/keywords/research`, body)
        .then((r) => r.data.research),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keyword-research-latest', domain._id] });
      qc.invalidateQueries({ queryKey: ['keyword-research-history', domain._id] });
    },
  });

  const bulkTrackMutation = useMutation({
    mutationFn: (body) =>
      api
        .post(`/api/domains/${domain._id}/keywords/bulk-track`, body)
        .then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['keyword-targets', domain._id] });
      const added = data?.tracked ?? data?.count ?? '';
      toast.success(added ? `Tracking ${added} new keyword${added === 1 ? '' : 's'}` : 'Keywords tracked');
    },
    onError: (err) => toast.error(err, 'Could not track keywords'),
  });

  const updateTargetMutation = useMutation({
    mutationFn: ({ id, body }) =>
      api
        .put(`/api/domains/${domain._id}/keywords/${id}`, body)
        .then((r) => r.data.target),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['keyword-targets', domain._id] });
      if (vars?.body?.status) toast.success(`Marked as ${vars.body.status}`);
    },
    onError: (err) => toast.error(err, 'Could not update keyword'),
  });

  const deleteTargetMutation = useMutation({
    mutationFn: (id) =>
      api.delete(`/api/domains/${domain._id}/keywords/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keyword-targets', domain._id] });
      toast.success('Keyword removed');
    },
    onError: (err) => toast.error(err, 'Could not remove keyword'),
  });

  const createTargetMutation = useMutation({
    mutationFn: (body) =>
      api
        .post(`/api/domains/${domain._id}/keywords`, body)
        .then((r) => r.data.target),
    onSuccess: (target) => {
      qc.invalidateQueries({ queryKey: ['keyword-targets', domain._id] });
      toast.success(`Tracking "${target.keyword}"`);
    },
    // Error handled inline by ManualAddCard — no toast (it shows next to the input).
  });

  const research = latestResearchQuery.data;
  const targets = targetsQuery.data || [];

  const onSubmitResearch = (e) => {
    e.preventDefault();
    if (!seed.trim()) return;
    researchMutation.mutate({ seed: seed.trim() });
  };

  const filteredTargets =
    statusFilter === 'all'
      ? targets
      : targets.filter((t) => t.status === statusFilter);

  const targetCountByStatus = useMemo(() => {
    const out = { all: targets.length };
    for (const t of targets) out[t.status] = (out[t.status] || 0) + 1;
    return out;
  }, [targets]);

  return (
    <div className="space-y-6">
      <KeywordsHero
        research={research}
        targetsCount={targets.length}
        running={researchMutation.isPending}
        seed={seed}
        setSeed={setSeed}
        onSubmit={onSubmitResearch}
      />

      {researchMutation.isError && (
        <ResearchError
          error={researchMutation.error}
          domain={domain}
          onDismiss={() => researchMutation.reset()}
        />
      )}

      <DisclaimerCard />

      {research && (
        <ResearchResults
          research={research}
          targets={targets}
          domain={domain}
          onBulkTrack={(keywords) =>
            bulkTrackMutation.mutate({ research_id: research._id, keywords })
          }
          onTrackOne={(keyword) =>
            createTargetMutation.mutate({
              keyword: keyword.keyword,
              intent: keyword.intent,
              is_question: keyword.is_question,
              difficulty: keyword.difficulty,
              ai_likelihood: keyword.ai_likelihood,
              google_likelihood: keyword.google_likelihood,
              channel_recommendation: keyword.channel_recommendation,
              rationale: keyword.rationale,
              source: 'research',
              research_id: research._id,
              status: 'researching',
            })
          }
          bulkPending={bulkTrackMutation.isPending}
        />
      )}

      <ManualAddCard
        onAdd={(kw) =>
          createTargetMutation.mutate({
            keyword: kw,
            source: 'manual',
            status: 'researching',
          })
        }
        pending={createTargetMutation.isPending}
        error={createTargetMutation.error?.response?.data?.error}
      />

      <TargetsList
        targets={filteredTargets}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        counts={targetCountByStatus}
        onUpdateStatus={(id, status) =>
          updateTargetMutation.mutate({ id, body: { status } })
        }
        onDelete={(id) => {
          if (window.confirm('Remove this keyword from your targets?')) {
            deleteTargetMutation.mutate(id);
          }
        }}
      />

      {researchHistoryQuery.data?.length > 1 && (
        <ResearchHistory runs={researchHistoryQuery.data} currentId={research?._id} />
      )}
    </div>
  );
}

/* ---------- Hero ---------- */

function KeywordsHero({ research, targetsCount, running, seed, setSeed, onSubmit }) {
  return (
    <div className="relative rounded-2xl overflow-hidden bg-ink-gradient text-white p-6 sm:p-8 shadow-xl">
      <div className="absolute inset-0 opacity-30 bg-brand-radial" />
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-30" />
      <div className="relative">
        <div className="text-xs uppercase tracking-wider text-white/60 mb-1 flex items-center gap-1.5">
          <Search className="w-3 h-3" /> Keyword research
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold">
          {research
            ? `${research.keywords?.length || 0} keywords found for "${research.seed}"`
            : 'Find keywords that work for both AI and Google'}
        </h2>
        <p className="text-sm text-white/70 mt-1 max-w-xl">
          Paste a seed topic and AI returns 20-25 keyword ideas — each labeled by intent,
          difficulty, and whether it's a better AI-search or Google bet. Google Suggest seeds
          the analysis with real autocomplete strings when available.
        </p>
        <form onSubmit={onSubmit} className="mt-4 flex flex-col sm:flex-row gap-2 max-w-2xl">
          <input
            className="flex-1 rounded-lg bg-white/15 text-white border border-white/25 placeholder:text-white/50 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-white/40 focus:bg-white/25"
            placeholder="Seed topic — e.g. 'AEO services for SaaS'"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
          />
          <button
            type="submit"
            disabled={running || seed.trim().length < 2}
            className="inline-flex items-center gap-2 rounded-lg bg-white text-ink-900 px-4 py-2.5 text-sm font-semibold hover:bg-white/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {running ? (
              <>
                <Activity className="w-4 h-4 animate-pulse" /> Researching…
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" /> Generate ideas
              </>
            )}
          </button>
        </form>
        {targetsCount > 0 && (
          <div className="text-xs text-white/60 mt-3">
            {targetsCount} {targetsCount === 1 ? 'keyword' : 'keywords'} tracked across all
            statuses
          </div>
        )}
      </div>
    </div>
  );
}

function ResearchError({ error, domain, onDismiss }) {
  const message = error?.response?.data?.error || error?.message || 'Research failed';
  const needsKey = /anthropic api key/i.test(message);
  return (
    <ErrorBanner
      title="Keyword research failed"
      body={
        <>
          {message}
          {needsKey && (
            <div className="mt-2">
              <Link to="../settings" className="underline font-medium">
                Open Settings → Anthropic API key
              </Link>{' '}
              to add yours.
            </div>
          )}
        </>
      }
      onDismiss={onDismiss}
    />
  );
}

function DisclaimerCard() {
  return (
    <div className="rounded-xl border border-amber-100 bg-amber-50/60 text-amber-900 text-xs p-3 flex items-start gap-2">
      <HelpCircle className="w-4 h-4 flex-none mt-0.5" />
      <div>
        <strong>Heads up on the numbers:</strong> difficulty and channel-likelihood are LLM
        estimates, not licensed SERP data (Ahrefs / Moz / DataForSEO). Use them to prioritize,
        not to bid. Google Suggest results — when they appear — are real Google autocomplete
        data.
      </div>
    </div>
  );
}

/* ---------- Research results ---------- */

function ResearchResults({ research, targets, onBulkTrack, onTrackOne, bulkPending, domain }) {
  const [selected, setSelected] = useState(new Set());
  const trackedKeywords = useMemo(
    () => new Set(targets.map((t) => t.keyword)),
    [targets],
  );
  const [showSuggest, setShowSuggest] = useState(false);

  const keywords = research.keywords || [];

  const toggle = (kw) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(kw)) next.delete(kw);
      else next.add(kw);
      return next;
    });
  };

  const onBulk = () => {
    if (!selected.size) return;
    onBulkTrack([...selected]);
    setSelected(new Set());
  };

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Sparkles className="w-4 h-4" />
          </span>
          Latest research
          <AIBadge label="AI" />
          <InfoTip
            title="Reading the badges"
            body="Channel = where the keyword likely converts (AI / Google / both). Difficulty = how hard to rank. AI likelihood / Google likelihood = a 0–100 estimate of how often this query gets searched in each engine. All numbers are LLM estimates — directional only."
          />
        </div>
        <div className="text-xs text-ink-400 flex items-center gap-3 flex-wrap">
          {research.category && (
            <span className="pill-muted">{research.category}</span>
          )}
          <span>Generated {formatRelative(research.createdAt)}</span>
          {research.estimated_cost != null && (
            <span>· est ${Number(research.estimated_cost).toFixed(4)}</span>
          )}
          <CsvExportButton domainId={domain._id} kind="keywords" label="Export all CSV" />
        </div>
      </div>

      {/* Google Suggest panel */}
      {(research.suggest_results?.length > 0 || research.suggest_status === 'failed') && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowSuggest((v) => !v)}
            className="text-xs font-semibold uppercase tracking-wider text-ink-400 flex items-center gap-1.5 hover:text-ink-900"
          >
            {showSuggest ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Google Suggest seeds
            {research.suggest_results?.length > 0 && (
              <span className="text-ink-700 normal-case tracking-normal font-normal">
                ({research.suggest_results.length})
              </span>
            )}
            {research.suggest_status === 'failed' && (
              <span className="pill-warn ml-1 normal-case">Fetch failed</span>
            )}
          </button>
          {showSuggest && research.suggest_results?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {research.suggest_results.map((s, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs bg-amber-50 text-amber-700 border border-amber-100"
                >
                  <Search className="w-3 h-3" />
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {selected.size > 0 && (
        <div className="rounded-xl border border-brand-100 bg-brand-50/60 p-3 mb-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-brand-800">
            <strong>{selected.size}</strong> selected
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-ghost text-xs" onClick={() => setSelected(new Set())}>
              Clear
            </button>
            <button className="btn-primary text-xs" onClick={onBulk} disabled={bulkPending}>
              <Plus className="w-3.5 h-3.5" />
              {bulkPending ? 'Tracking…' : `Track ${selected.size}`}
            </button>
          </div>
        </div>
      )}

      {keywords.length === 0 ? (
        <p className="text-sm text-ink-400">
          The model didn't return usable keyword data for this run. Try a more specific seed.
        </p>
      ) : (
        <ul className="divide-y divide-gray-50">
          {keywords.map((k, i) => (
            <KeywordResultRow
              key={`${k.keyword}-${i}`}
              keyword={k}
              selected={selected.has(k.keyword)}
              tracked={trackedKeywords.has(k.keyword)}
              onToggle={() => toggle(k.keyword)}
              onTrack={() => onTrackOne(k)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function KeywordResultRow({ keyword, selected, tracked, onToggle, onTrack }) {
  const ch = CHANNEL_META[keyword.channel_recommendation] || CHANNEL_META.unknown;
  const ChIcon = ch.icon;
  const diff = DIFFICULTY_META[keyword.difficulty] || DIFFICULTY_META.unknown;

  return (
    <li className="py-3 flex items-start justify-between gap-3 first:pt-0 last:pb-0">
      <label className="flex items-start gap-3 min-w-0 flex-1 cursor-pointer">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="mt-1 h-4 w-4 flex-none rounded border-gray-300 text-brand-600 focus:ring-brand-500/30"
        />
        <div className="min-w-0">
          <div className="font-medium text-ink-900 truncate flex items-center gap-2">
            {keyword.is_question && (
              <HelpCircle className="w-3.5 h-3.5 text-brand-600 flex-none" />
            )}
            {keyword.keyword}
            {tracked && (
              <span className="pill-success">
                <CheckCircle2 className="w-3 h-3" /> Tracked
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-1">
            <span className={`pill border ${ch.class}`}>
              <ChIcon className="w-3 h-3" /> {ch.label}
            </span>
            <span className={`pill ${diff.class}`}>{diff.label} difficulty</span>
            <span className="pill-muted">{INTENT_LABEL[keyword.intent] || '—'}</span>
            <LikelihoodChip label="AI" value={keyword.ai_likelihood} />
            <LikelihoodChip label="Google" value={keyword.google_likelihood} />
          </div>
          {keyword.rationale && (
            <p className="text-xs text-ink-400 mt-1.5 leading-relaxed">
              {keyword.rationale}
            </p>
          )}
        </div>
      </label>
      <div className="flex-none">
        {tracked ? (
          <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Saved
          </span>
        ) : (
          <button className="btn-secondary text-xs" onClick={onTrack}>
            <Plus className="w-3.5 h-3.5" /> Track
          </button>
        )}
      </div>
    </li>
  );
}

function LikelihoodChip({ label, value }) {
  const tone = {
    high: 'bg-emerald-50 text-emerald-700',
    medium: 'bg-amber-50 text-amber-700',
    low: 'bg-gray-100 text-gray-600',
    unknown: 'bg-gray-100 text-gray-600',
  }[value || 'unknown'];
  return (
    <span className={`pill ${tone}`} title={`Likelihood of being typed into ${label}`}>
      {label} {value || '—'}
    </span>
  );
}

/* ---------- Manual add ---------- */

function ManualAddCard({ onAdd, pending, error }) {
  const [kw, setKw] = useState('');
  const submit = (e) => {
    e.preventDefault();
    if (!kw.trim()) return;
    onAdd(kw.trim().toLowerCase());
    setKw('');
  };
  return (
    <form onSubmit={submit} className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Plus className="w-4 h-4" />
          </span>
          Track a keyword manually
        </div>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
          <input
            className="input pl-10"
            placeholder="Type any keyword and press Enter"
            value={kw}
            onChange={(e) => setKw(e.target.value)}
          />
        </div>
        <button type="submit" className="btn-primary" disabled={pending}>
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      {error && (
        <div className="mt-2 text-xs text-rose-600">{error}</div>
      )}
    </form>
  );
}

/* ---------- Targets list ---------- */

function TargetsList({
  targets,
  statusFilter,
  setStatusFilter,
  counts,
  onUpdateStatus,
  onDelete,
}) {
  const filters = ['all', 'researching', 'targeted', 'won', 'archived'];

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Star className="w-4 h-4" />
          </span>
          Your keyword targets
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Filter className="w-3 h-3 text-ink-400" />
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={clsx(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border transition-colors',
                statusFilter === f
                  ? 'bg-brand-50 text-brand-700 border-brand-200'
                  : 'bg-white text-ink-700 border-gray-200 hover:border-brand-200',
              )}
            >
              {f === 'all' ? 'All' : STATUS_META[f]?.label || f}
              <span className="text-ink-400">{counts[f] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {targets.length === 0 ? (
        <p className="text-sm text-ink-400">
          No keywords saved at this status yet. Run research above or add manually.
        </p>
      ) : (
        <ul className="divide-y divide-gray-50">
          {targets.map((t) => (
            <TargetRow
              key={t._id}
              target={t}
              onUpdateStatus={onUpdateStatus}
              onDelete={onDelete}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function TargetRow({ target, onUpdateStatus, onDelete }) {
  const ch = CHANNEL_META[target.channel_recommendation] || CHANNEL_META.unknown;
  const ChIcon = ch.icon;
  const diff = DIFFICULTY_META[target.difficulty] || DIFFICULTY_META.unknown;
  return (
    <li className="py-3 flex items-start justify-between gap-3 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-ink-900 truncate flex items-center gap-2">
          {target.is_question && (
            <HelpCircle className="w-3.5 h-3.5 text-brand-600 flex-none" />
          )}
          {target.keyword}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          <span className={`pill border ${ch.class}`}>
            <ChIcon className="w-3 h-3" /> {ch.label}
          </span>
          <span className={`pill ${diff.class}`}>{diff.label} diff</span>
          <span className="pill-muted">{INTENT_LABEL[target.intent] || '—'}</span>
          <span className="pill-muted text-[10px]">
            {target.source === 'research' ? 'From research' : 'Manual'}
          </span>
        </div>
        {target.rationale && (
          <p className="text-xs text-ink-400 mt-1.5 leading-relaxed">
            {target.rationale}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-none">
        <select
          value={target.status}
          onChange={(e) => onUpdateStatus(target._id, e.target.value)}
          className={clsx(
            'text-xs rounded-lg px-2 py-1 border bg-white',
            STATUS_META[target.status]?.class,
            'border-current/30',
          )}
        >
          {Object.entries(STATUS_META).map(([id, m]) => (
            <option key={id} value={id}>
              {m.label}
            </option>
          ))}
        </select>
        <button
          className="btn-icon hover:!text-rose-600 hover:!bg-rose-50"
          title="Remove"
          onClick={() => onDelete(target._id)}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}

/* ---------- Research history ---------- */

function ResearchHistory({ runs, currentId }) {
  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <History className="w-4 h-4" />
          </span>
          Recent research
        </div>
        <span className="text-xs text-ink-400">Last {Math.min(runs.length, 10)}</span>
      </div>
      <ul className="divide-y divide-gray-50 text-sm">
        {runs.slice(0, 10).map((r) => (
          <li
            key={r._id}
            className="py-2.5 flex items-center justify-between gap-3 first:pt-0 last:pb-0"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span
                className={clsx(
                  'flex-none h-8 w-8 rounded-lg flex items-center justify-center font-bold text-xs',
                  r.status === 'failed'
                    ? 'bg-rose-50 text-rose-600'
                    : 'bg-brand-50 text-brand-700',
                )}
              >
                {r.status === 'failed' ? '!' : '✓'}
              </span>
              <div className="min-w-0">
                <div className="text-ink-800">
                  "{r.seed}"
                  {r._id === currentId && (
                    <span className="pill-brand ml-2">Current</span>
                  )}
                </div>
                <div className="text-xs text-ink-400">
                  {r.status === 'failed'
                    ? r.error || 'Failed'
                    : `${formatRelative(r.createdAt)} · ${r.category || ''}`}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
